import { env } from '@/lib/env';
import { isPublicPath } from '@/lib/public-routes';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from 'sonner';

function extractMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  if (typeof b.message === 'string' && b.message.length > 0) return b.message;
  if (typeof b.detail === 'string' && b.detail.length > 0) return b.detail;
  return undefined;
}

// Sessão perdida: manda pro login, exceto em rota pública — lá o fluxo em
// andamento (callback do SSO, convite, redefinição de senha) não pode cair.
function redirectToLogin() {
  if (isPublicPath(window.location.pathname)) return;
  window.location.href = '/login';
}

export class ApiError extends Error {
  public fieldErrors?: Record<string, string[]>;
  public nonFieldErrors?: string[];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(public status: number, public body: any) {
    super(extractMessage(body) ?? `API Error: ${status}`);
    if (body && typeof body === 'object') {
      const b = body as Record<string, unknown>;
      if (
        b.field_errors &&
        typeof b.field_errors === 'object' &&
        !Array.isArray(b.field_errors)
      ) {
        this.fieldErrors = b.field_errors as Record<string, string[]>;
      }
      if (Array.isArray(b.non_field_errors)) {
        this.nonFieldErrors = b.non_field_errors as string[];
      }
    }
  }
}

export class BaseApiClient {
  private baseUrl = env.VITE_API_URL;
  private isRefreshing = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private failedQueue: { resolve: (value: unknown) => void; reject: (reason?: any) => void; }[] = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private processQueue = (error: any, token: string | null = null) => {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    this.failedQueue = [];
  }

  public async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isPublic = false,
    silent403 = false
  ): Promise<T> {
    const headers = {
      ...options.headers,
    } as Record<string, string>;

    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (!isPublic) {
      const token = useAuthStore.getState().token;

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    let response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorBody;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { detail: 'Ocorreu um erro inesperado no servidor.' };
      }

      const isTokenError = response.status === 401 || (response.status === 403 && errorBody.code === 'token_not_valid');

      if (!isPublic && isTokenError) {
        if (!this.isRefreshing) {
          this.isRefreshing = true;
          const refreshToken = useAuthStore.getState().refreshToken;

          if (refreshToken) {
            try {
              const { access, refresh: newRefresh } = await this.refreshToken(refreshToken);
              useAuthStore.getState().setToken(access, newRefresh ?? refreshToken);
              headers['Authorization'] = `Bearer ${access}`;
              this.processQueue(null, access);
              response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...options,
                headers,
              });
              return response.json();
            } catch (refreshError) {
              this.processQueue(refreshError, null);
              useAuthStore.setState({ user: null, token: null, refreshToken: null });
              redirectToLogin();
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          } else {
            useAuthStore.setState({ user: null, token: null, refreshToken: null });
            redirectToLogin();
            return Promise.reject(new ApiError(response.status, { detail: 'No refresh token available', code: 'token_not_valid' }));
          }
        }

        return new Promise((resolve, reject) => {
          this.failedQueue.push({ resolve, reject });
        }).then(token => {
          headers['Authorization'] = `Bearer ${token}`;
          return fetch(`${this.baseUrl}${endpoint}`, {
            ...options,
            headers,
          }).then(res => res.json());
        });
      }

      if (response.status === 403 && !silent403) {
        toast.error(errorBody.detail || 'Você não tem permissão para executar essa ação.');
      }

      throw new ApiError(response.status, errorBody);
    }

    if (response.status === 204) {
      return null as T;
    }

    // Algumas rotas (ex: mark-all-read) retornam 200/202 sem body. response.json()
    // explode com "Unexpected end of JSON input" — tratamos como null pra não estourar UI.
    try {
      return await response.json();
    } catch {
      return null as T;
    }
  }

  async refreshToken(refresh: string): Promise<{ access: string; refresh?: string }> {
    const response = await this.request<{ access: string; refresh?: string }>(
      '/auth/jwt/refresh/',
      {
        method: 'POST',
        body: JSON.stringify({ refresh }),
      },
      true
    );
    return response;
  }
}

export const baseApi = new BaseApiClient();
